import { Component, EventEmitter, Input, OnChanges,ChangeDetectorRef,SimpleChanges, OnInit, Output } from '@angular/core';
import { CurrentUserStateService } from 'quangular';
import { TearsheetUnit } from '@common/enums/tearsheet/TearsheetState';
import { UserRole } from '@common/enums/UserRole';
import { IMetricProps } from '@common/models/ITearSheet';
import { getQuarter, utcToLocalDate, localDateToUtcTimestamp } from '@common/services/DateHelper';
import TearsheetHelperService from '@common/services/TearsheetHelperService';
import { REMAINING_TERM_METRIC_CODE } from '@common/constants/tearsheet.constants';
import { Utils } from '@common/utils/utils';

interface IMetricPropsExtended extends IMetricProps {
  isDateInput?: boolean;
}
@Component({
  selector: 'app-metric',
  templateUrl: './metric.component.html',
  styleUrls: ['./metric.component.scss']
})


export class MetricComponent implements OnInit, OnChanges {

  tearsheetUnit = TearsheetUnit;
  remainingTermMetricCode = REMAINING_TERM_METRIC_CODE;

  @Input() metric: IMetricPropsExtended = {
    displayName: '',
    value: 'NA',
    unit: this.tearsheetUnit.unitless,
    isDateInput: false
  }
  @Input() editKeySet: any;
  @Input() helper: TearsheetHelperService
  @Input() millions: boolean;
  @Input() isEditMode: boolean;
  @Input() showNAForInvalid: boolean = false;
  @Input() showNAForZero: boolean = false;
  @Output() metricEditedByUser = new EventEmitter();
  timeout: any = null;
  percentEditKey ;
  currentUserRole: string;
  hideTooltip:  boolean = true
  originalDate: number | null = null;
  @Input() firstInvestedDate;

  remainingTermMonths: number | null = null;
  remainingTermDays: number | null = null;
  calculatedRemainingTermYears: string | null = null;
  daysExceededMax = false;

  constructor(private readonly currentUserStateService: CurrentUserStateService,private readonly cdr: ChangeDetectorRef){

  }
  ngOnInit(): void {
    const currentUser = this.currentUserStateService.value;
    this.currentUserRole = currentUser?.roles[0] ;
    if(currentUser?.roles[0] === UserRole.Admin){
      this.hideTooltip = false
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['metric']) {
      if (this.metric?.isDateInput) {
        this.initializeMetricValue();

        const val = this.metric.value;
        this.originalDate = val instanceof Date
          ? val.getTime()
          : new Date(val as string | number).getTime();
      }

      if (this.metric?.metricCode === REMAINING_TERM_METRIC_CODE) {
        this.initializeRemainingTermValue();
      } else {
        this.remainingTermMonths = null;
        this.remainingTermDays = null;
        this.calculatedRemainingTermYears = null;
      }
    }
  }

  updateMetricValue(eventOrValue: any) {
    const value = eventOrValue?.value ?? eventOrValue ?? null;
    if (this.metric?.isDateInput && value instanceof Date) {
      const newDate = value.getTime();
      if (!Number.isNaN(newDate) && this.originalDate !== newDate) {
        const updatedMetric = {
          ...this.metric,
          value,
          displayName:
            this.firstInvestedDate &&
              new Date(this.firstInvestedDate).toDateString() === value.toDateString()
              ? 'Investment Date'
              : 'Commitment Date',
        };

        this.metric = updatedMetric;
        const utcTimestamp = localDateToUtcTimestamp(value);
        this.metricEditedByUser.emit({
          ...updatedMetric,
          value: utcTimestamp,
        });
        this.cdr.detectChanges();
        this.originalDate = newDate;
      }
      // Date input fully handled above — skip executeListing to avoid a second emit
      return;
    }

    this.executeListing(value);
  }


  initializeMetricValue() {
    if (!this.metric?.isDateInput) return;

    let val = this.metric.value;

    // Already a valid Date
    if (val instanceof Date && !Number.isNaN(val.getTime())) return;

    // If value is a timestamp
    if (typeof val === 'number') {
      this.metric.value = utcToLocalDate(val);
      return;
    }

    // If value is a string
    if (typeof val === 'string') {
      const parsed = new Date(val);
      if (!Number.isNaN(parsed.getTime())) {
        this.metric.value = utcToLocalDate(parsed);
        return;
      }
    }

    // Fallback
    this.metric.value = new Date();
  }

  private initializeRemainingTermValue(): void {
    const val = this.metric?.value;
    if (typeof val === 'string') {
      const parsed = Utils.parseRemainingTermValue(val);
      if (parsed) {
        this.remainingTermMonths = parsed.months || null;
        this.remainingTermDays = parsed.days || null;
        this.calculatedRemainingTermYears = this.computeRemainingTermYears();
        this.cdr.detectChanges();
        return;
      }
    }
    this.remainingTermMonths = null;
    this.remainingTermDays = null;
    this.calculatedRemainingTermYears = null;
    this.cdr.detectChanges();
  }

  getRemainingTermDisplay(value: any): string {
    return Utils.getRemainingTermDisplay(value);
  }

  updateRemainingTerm(): void {
    this.calculatedRemainingTermYears = this.computeRemainingTermYears();
    const hasValue = this.remainingTermMonths !== null || this.remainingTermDays !== null;
    const m = this.remainingTermMonths ?? 0;
    const d = this.remainingTermDays ?? 0;
    const storedValue = hasValue ? `${m}m ${d}d` : null;
    this.updateMetricValue({ value: storedValue });
  }

  onDaysChange(value: number | null): void {
    this.daysExceededMax = value !== null && value > 30;
    this.updateRemainingTerm();
  }

  onDaysBlur(): void {
    if (this.remainingTermDays !== null && this.remainingTermDays > 30) {
      this.remainingTermDays = 30;
      this.daysExceededMax = false;
      this.updateRemainingTerm();
      this.cdr.detectChanges();
    }
  }

  private computeRemainingTermYears(): string | null {
    if (this.remainingTermMonths === null && this.remainingTermDays === null) {
      return null;
    }
    return Utils.formatRemainingTerm(this.remainingTermMonths ?? 0, this.remainingTermDays ?? 0);
  }

  executeListing(val: any) {
    // Extract raw value if needed
    if (val && typeof val === 'object' && !(val instanceof Date)) {
      val = val.value ?? val.target?.value ?? null;
    }

    if (val === null) {
      val = '';
    } else if (this.metric.unit === this.tearsheetUnit.percent) {
      val = val / 100;
    }

    // When emitting to parent/API, send numeric or string if needed
    const apiValue =
      val instanceof Date ? val.getTime() : val;

    const { isDateInput, ...rest } = this.metric;
    const editedMetricProp: IMetricProps = {
      ...rest,
      value: apiValue
    };

    this.metricEditedByUser.emit(editedMetricProp);
  }


  setUnit(unit) {
    switch (unit) {
      case this.tearsheetUnit.USD:
        return '$';
      case this.tearsheetUnit.percent:
      case this.tearsheetUnit.irr:
        return '%';
      case this.tearsheetUnit.ratio:
        return 'x';
      default:
        return '';
    }
  }

  isInvalidValue(value: any): boolean {
    return value === null || value === undefined || value === '' || value === 'null' || value === 'undefined' || value === '-' || (this.showNAForZero && value === 0);
  }
  public get NullString(): string {
    return TearsheetHelperService.NullString;
  }
  getQuarter(date) {
    if(date){
      const today = new Date(date);
      let quarter = getQuarter(today) ;
      return quarter;
    }
    return 'NA';
  }
}
